import React from 'react';
import momentPropTypes from 'react-moment-proptypes';
import ReactDOM from 'react-dom';
import moment from 'moment';
import cx from 'classnames';
import { Portal } from 'react-portal';
import { forbidExtraProps } from 'airbnb-prop-types';
import { addEventListener, removeEventListener } from 'consolidated-events';
import isTouchDevice from 'is-touch-device';
import { DayPickerRangeController, isInclusivelyAfterDay, DateRangePickerShape } from 'react-dates';
import { DateRangePickerPhrases } from './utils/defaultPhrases';
import getResponsiveContainerStyles from './utils/getResponsiveContainerStyles';

import OutsideClickHandler from './OutsideClickHandler';
import DateRangePickerInputController from './DateRangePickerInputController';
import { IconClose as CloseButton, IconArrowBack, IconArrowForward } from '../icons/Icons';

import {
    START_DATE,
    END_DATE,
    HORIZONTAL_ORIENTATION,
    VERTICAL_ORIENTATION,
    ANCHOR_LEFT,
    ANCHOR_RIGHT,
    OPEN_DOWN,
    OPEN_UP,
    DAY_SIZE,
    ICON_BEFORE_POSITION
} from './constants';

const propTypes = forbidExtraProps({
    ...DateRangePickerShape,
    lowerLimit: momentPropTypes.momentObj,
    upperLimit: momentPropTypes.momentObj
});

const defaultProps = {
    // required props for a functional interactive DateRangePicker
    startDate: null,
    endDate: null,
    focusedInput: null,
    
    lowerLimit: null,
    upperLimit: null,

    // input related props
    startDateId: START_DATE,
    startDatePlaceholderText: 'Check in',
    endDateId: END_DATE,
    endDatePlaceholderText: 'Check out',
    disabled: false,
    required: false,
    readOnly: false,
    screenReaderInputMessage: '',
    showClearDates: false,
    showDefaultInputIcon: false,
    inputIconPosition: ICON_BEFORE_POSITION,
    customInputIcon: null,
    customArrowIcon: null,
    customCloseIcon: null,

    // calendar presentation and interaction related props
    renderMonth: null,
    orientation: HORIZONTAL_ORIENTATION,
    anchorDirection: ANCHOR_LEFT,
    openDirection: OPEN_DOWN,
    horizontalMargin: 0,
    withPortal: false,
    withFullScreenPortal: false,
    initialVisibleMonth: null,
    numberOfMonths: 2,
    keepOpenOnDateSelect: false,
    reopenPickerOnClearDates: false,
    renderCalendarInfo: null,
    hideKeyboardShortcutsPanel: false,
    daySize: DAY_SIZE,
    isRTL: false,
    firstDayOfWeek: null,

    // navigation related props
    navPrev: null,
    navNext: null,

    onPrevMonthClick() {},
    onNextMonthClick() {},

    onClose() {},

    // day presentation and interaction related props
    renderDay: null,
    minimumNights: 1,
    enableOutsideDays: false,
    isDayBlocked: () => false,
    isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
    isDayHighlighted: () => false,

    // internationalization
    displayFormat: () => moment.localeData().longDateFormat('L'),
    monthFormat: 'MMMM YYYY',
    weekDayFormat: 'dd',
    phrases: DateRangePickerPhrases
};

export default class DateRangePicker extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            dayPickerContainerStyles: {},
            isDateRangePickerInputFocused: !!props.focusedInput,
            isDayPickerFocused: false,
            showKeyboardShortcuts: true
        };

        this.isTouchDevice = false;

        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.onDateRangePickerInputFocus = this.onDateRangePickerInputFocus.bind(this);
        this.onDayPickerFocus = this.onDayPickerFocus.bind(this);
        this.onDayPickerBlur = this.onDayPickerBlur.bind(this);
        this.showKeyboardShortcutsPanel = this.showKeyboardShortcutsPanel.bind(this);

        this.responsivizePickerPosition = this.responsivizePickerPosition.bind(this);

        this.setDayPickerContainerRef = this.setDayPickerContainerRef.bind(this);
        this.setDayPickerRef = this.setDayPickerRef.bind(this);
    }

    componentDidMount() {
        this.resizeHandle = addEventListener(window, 'resize', this.responsivizePickerPosition, { passive: true });
        this.responsivizePickerPosition();
        this.isTouchDevice = isTouchDevice();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.focusedInput && this.props.focusedInput && this.isOpened()) {
            // The date picker just changed from being closed to being open.
            this.responsivizePickerPosition();
        }
    }

    componentWillUnmount() {
        if (this.resizeHandle) {
            removeEventListener(this.resizeHandle);
        }
    }

    onOutsideClick() {
        const { onFocusChange, onClose, startDate, endDate } = this.props;
        if (!this.isOpened()) {
            return;
        }

        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: false,
            showKeyboardShortcuts: false
        });

        onFocusChange(null);
        onClose({ startDate, endDate });
    }

    onDateRangePickerInputFocus(focusedInput) {
        const { onFocusChange, withPortal, withFullScreenPortal } = this.props;

        if (focusedInput) {
            const moveFocusToDayPicker = withPortal || withFullScreenPortal || this.isTouchDevice;
            if (moveFocusToDayPicker) {
                this.onDayPickerFocus();
            }
        else {
                this.onDayPickerBlur();
            }
        }

        onFocusChange(focusedInput);
    }

    onDayPickerFocus() {
        const { focusedInput, onFocusChange } = this.props;
        if (!focusedInput) {
            onFocusChange(START_DATE);
        }

        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: true,
            showKeyboardShortcuts: false
        });
    }

    onDayPickerBlur() {
        this.setState({
            isDateRangePickerInputFocused: true,
            isDayPickerFocused: false,
            showKeyboardShortcuts: false
        });
    }

    getDayPickerContainerClasses() {
        const { orientation, withPortal, withFullScreenPortal, anchorDirection, openDirection, isRTL } = this.props;

        const dayPickerClassName = cx('DateRangePicker__picker', {
            'DateRangePicker__picker--direction-left': anchorDirection === ANCHOR_LEFT,
            'DateRangePicker__picker--direction-right': anchorDirection === ANCHOR_RIGHT,
            'DateRangePicker__picker--open-down': openDirection === OPEN_DOWN,
            'DateRangePicker__picker--open-up': openDirection === OPEN_UP,
            'DateRangePicker__picker--horizontal': orientation === HORIZONTAL_ORIENTATION,
            'DateRangePicker__picker--vertical': orientation === VERTICAL_ORIENTATION,
            'DateRangePicker__picker--portal': withPortal || withFullScreenPortal,
            'DateRangePicker__picker--full-screen-portal': withFullScreenPortal,
            'DateRangePicker__picker--rtl': isRTL
        });

        return dayPickerClassName;
    }

    getDayPickerDOMNode() {
        return ReactDOM.findDOMNode(this.dayPicker); // eslint-disable-line react/no-find-dom-node
    }

    setDayPickerContainerRef(ref) {
        this.dayPickerContainer = ref;
    }

    setDayPickerRef(ref) {
        this.dayPicker = ref;
    }

    isOpened() {
        const { focusedInput } = this.props;
        return focusedInput === START_DATE || focusedInput === END_DATE;
    }

    responsivizePickerPosition() {
        // It's possible the portal props have been changed in response to window resizes
        // So let's ensure we reset this back to the base state each time
        this.setState({ dayPickerContainerStyles: {} });

        if (!this.isOpened()) {
            return;
        }

        const { anchorDirection, horizontalMargin, withPortal, withFullScreenPortal } = this.props;
        const { dayPickerContainerStyles } = this.state;

        const isAnchoredLeft = anchorDirection === ANCHOR_LEFT;
        if (!withPortal && !withFullScreenPortal) {
            const containerRect = this.dayPickerContainer.getBoundingClientRect();
            const currentOffset = dayPickerContainerStyles[anchorDirection] || 0;
            const containerEdge = isAnchoredLeft ? containerRect[ANCHOR_RIGHT] : containerRect[ANCHOR_LEFT];

            this.setState({
                dayPickerContainerStyles: getResponsiveContainerStyles(anchorDirection, currentOffset, containerEdge, horizontalMargin)
            });
        }
    }

    showKeyboardShortcutsPanel() {
        this.setState({
            isDateRangePickerInputFocused: false,
            isDayPickerFocused: true,
            showKeyboardShortcuts: true
        });
    }

    maybeRenderDayPickerWithPortal() {
        const { withPortal, withFullScreenPortal } = this.props;

        if (!this.isOpened()) {
            return null;
        }

        if (withPortal || withFullScreenPortal) {
            return <Portal isOpened>{this.renderDayPicker()}</Portal>;
        }

        return this.renderDayPicker();
    }

    renderDayPicker() {
        const {
            isDayBlocked,
            isDayHighlighted,
            isOutsideRange,
            numberOfMonths,
            orientation,
            monthFormat,
            renderMonth,
            onPrevMonthClick,
            onNextMonthClick,
            onFocusChange,
            withPortal,
            withFullScreenPortal,
            daySize,
            enableOutsideDays,
            focusedInput,
            startDate,
            endDate,
            minimumNights,
            keepOpenOnDateSelect,
            renderDay,
            renderCalendarInfo,
            firstDayOfWeek,
            initialVisibleMonth,
            hideKeyboardShortcutsPanel,
            customCloseIcon,
            onClose,
            onDatesChange,
            phrases,
            isRTL,
            weekDayFormat
        } = this.props;

        const { dayPickerContainerStyles, isDayPickerFocused, showKeyboardShortcuts } = this.state;

        const onOutsideClick = !withFullScreenPortal && withPortal ? this.onOutsideClick : undefined;
        const initialVisibleMonthThunk = initialVisibleMonth || (() => startDate || endDate || moment());

        const closeIcon = customCloseIcon || <CloseButton />;
        const arrowIconBack = <IconArrowBack className="rec-icon-arrow-back" />;
        const arrowIconForward = <IconArrowForward className="rec-icon-arrow-forward" />;

        return (
            <div // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                ref={this.setDayPickerContainerRef}
                className={this.getDayPickerContainerClasses()}
                style={dayPickerContainerStyles}
                onClick={onOutsideClick}>
                <DayPickerRangeController
                    ref={this.setDayPickerRef}
                    orientation={orientation}
                    enableOutsideDays={enableOutsideDays}
                    numberOfMonths={numberOfMonths}
                    onPrevMonthClick={onPrevMonthClick}
                    onNextMonthClick={onNextMonthClick}
                    onDatesChange={onDatesChange}
                    onFocusChange={onFocusChange}
                    onClose={onClose}
                    focusedInput={focusedInput}
                    startDate={startDate}
                    endDate={endDate}
                    monthFormat={monthFormat}
                    renderMonth={renderMonth}
                    withPortal={withPortal || withFullScreenPortal}
                    daySize={daySize}
                    initialVisibleMonth={initialVisibleMonthThunk}
                    hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                    minimumNights={minimumNights}
                    isOutsideRange={isOutsideRange}
                    isDayHighlighted={isDayHighlighted}
                    isDayBlocked={isDayBlocked}
                    keepOpenOnDateSelect={keepOpenOnDateSelect}
                    renderDay={renderDay}
                    renderCalendarInfo={renderCalendarInfo}
                    isFocused={isDayPickerFocused}
                    showKeyboardShortcuts={showKeyboardShortcuts}
                    onBlur={this.onDayPickerBlur}
                    phrases={phrases}
                    isRTL={isRTL}
                    firstDayOfWeek={firstDayOfWeek}
                    weekDayFormat={weekDayFormat}
                    navPrev={arrowIconBack}
                    navNext={arrowIconForward} />

                {withFullScreenPortal && (
                    <button className="DateRangePicker__close" type="button" onClick={this.onOutsideClick} aria-label={phrases.closeDatePicker}>
                        <div className="DateRangePicker__close">{closeIcon}</div>
                    </button>
                )}
            </div>
        );
    }

    render() {
        const {
            startDate,
            startDateId,
            startDatePlaceholderText,
            endDate,
            endDateId,
            endDatePlaceholderText,
            focusedInput,
            showClearDates,
            showDefaultInputIcon,
            inputIconPosition,
            customInputIcon,
            customArrowIcon,
            customCloseIcon,
            disabled,
            required,
            readOnly,
            openDirection,
            phrases,
            isOutsideRange,
            isDayBlocked,
            upperLimit,
            lowerLimit,
            minimumNights,
            withPortal,
            withFullScreenPortal,
            displayFormat,
            reopenPickerOnClearDates,
            onClose,
            onDatesChange,
            isRTL
        } = this.props;

        const { isDateRangePickerInputFocused } = this.state;

        const onOutsideClick = !withPortal && !withFullScreenPortal ? this.onOutsideClick : undefined;

        return (
            <div className="DateRangePicker">
                <OutsideClickHandler onOutsideClick={onOutsideClick}>
                    <DateRangePickerInputController
                        startDate={startDate}
                        startDateId={startDateId}
                        startDatePlaceholderText={startDatePlaceholderText}
                        isStartDateFocused={focusedInput === START_DATE}
                        endDate={endDate}
                        endDateId={endDateId}
                        endDatePlaceholderText={endDatePlaceholderText}
                        isEndDateFocused={focusedInput === END_DATE}
                        displayFormat={displayFormat}
                        showClearDates={showClearDates}
                        showCaret={!withPortal && !withFullScreenPortal}
                        showDefaultInputIcon={showDefaultInputIcon}
                        inputIconPosition={inputIconPosition}
                        customInputIcon={customInputIcon}
                        customArrowIcon={customArrowIcon}
                        customCloseIcon={customCloseIcon}
                        disabled={disabled}
                        required={required}
                        readOnly={readOnly}
                        openDirection={openDirection}
                        reopenPickerOnClearDates={reopenPickerOnClearDates}
                        isOutsideRange={isOutsideRange}
                        isDayBlocked={isDayBlocked}
                        upperLimit={upperLimit}
                        lowerLimit={lowerLimit}
                        minimumNights={minimumNights}
                        withFullScreenPortal={withFullScreenPortal}
                        onDatesChange={onDatesChange}
                        onFocusChange={this.onDateRangePickerInputFocus}
                        onArrowDown={this.onDayPickerFocus}
                        onQuestionMark={this.showKeyboardShortcutsPanel}
                        onClose={onClose}
                        phrases={phrases}
                        isFocused={isDateRangePickerInputFocused}
                        isRTL={isRTL} />

                    {this.maybeRenderDayPickerWithPortal()}
                </OutsideClickHandler>
            </div>
        );
    }
}

DateRangePicker.propTypes = propTypes;
DateRangePicker.defaultProps = defaultProps;
